import { defineComponent as _defineComponent } from 'vue'
import { computed, inject, ref } from 'vue';

import { ObjectType, Offer } from '@/@types/graphql-types';
import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { UsePromotion } from '@/helpers/composables/usePromotion';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { ClickoutUrl, trackClickout } from '@/helpers/clickout-helper';
import {
	useAppStore,
	useConstantStore,
	useExperimentStore,
	useLanguageStore,
	useUserStore,
} from '@/helpers/composables/useStores';
import { OfferLocation } from '@/helpers/offer-helper';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { FALLBACK_PROMOTION_OFFER, getATVPlustItsctParam, primeImpactCountries } from '@/helpers/promotion-helper';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { useQuery } from '@/helpers/composables/useApollo';
import {
	GetPackageProviderPlansDocument,
	GetPackageProviderPlansQuery,
} from '@/components/buybox-new/graphql/GetPackageProviderPlans.query';
import { TitleOfferFragment } from '@/components/buybox/graphql/fragments/Offer.fragment';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import PromotionRowOfferDescription from '@/components/buybox-new/buyboxoffer/PromotionRowOfferPresentation.vue';
import PromotionRowOfferLabel from '@/components/buybox-new/buyboxoffer/PromotionRowOfferLabel.vue';
import PromotionRowCTA from '@/components/buybox-new/buyboxoffer/PromotionRowCTA.vue';
import JwTVModal from '@/ui-components/JwTVModal.vue';
import { SnowplowContext, SnowplowTitleContextGraphql } from '@/helpers/tracking/providers';

import { FTSwiperExpVariants } from '../experiments/FreeTrialSwiperExp';
import { BuyBoxFilterOptions } from '@/stores/user.store';
import { getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { OFFER_ENTITYID_KEY } from '@/helpers/composables/useProvideClickoutPlacement';

import { useBuyboxLoggedInExp } from '../experiments/BuyboxLoggedInExp/composables/useBuyboxLoggedInExp';

// GOLDEN_EXP
import {
	BuyboxGoldenExp,
	BuyboxGoldenExpVariants,
	getGolgenExpTagInformation,
} from '@/components/experiments/BuyboxGoldenExp2';
import { useMounted } from '@vueuse/core';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import { ModalHelper } from '@/helpers/modal-helper';
// GOLDEN_EXP
import { AppleChannelsExpVariants, getAppleChannelsExpAmazonTag } from '@/components/experiments/AppleChannelsExp';
import { useSingleQtvExp } from '../experiments/SingleQtvExp/composables/useSingleQtvExp';

export type PlanOffer = ReturnType<typeof getPlanOffer>;


export default /*#__PURE__*/_defineComponent({
  __name: 'PromotionRowNew',
  props: {
    title: null,
    offers: { default: () => [] },
    additionalContexts: { default: () => [] },
    hardcodedFallbackOfferId: null,
    ftSwiperExpVariant: null,
    appleChannelsExpVariant: null
  },
  emits: ["clickOut", "impressionCallback"],
  setup(__props: any, { emit }: { emit: ({
	(
		e: 'clickOut',
		payload: {
			buyboxOffer: UsePromotion['promotionOffer'];
			offerLocation: OfferLocation.FREE_TRIAL;
			row: HTMLElement | null;
		}
	): void;
	(e: 'impressionCallback'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { title: TitleDetail, offers: Offer[], additionalContexts: SnowplowContext[], hardcodedFallbackOfferId?: number, ftSwiperExpVariant?: FTSwiperExpVariants, appleChannelsExpVariant?: AppleChannelsExpVariants };





const container = ref<HTMLElement | null>(null);
const titleContext = inject('titleContext', ref<SnowplowTitleContextGraphql | null>(null));
const filterOption = inject('filterOption', ref<BuyBoxFilterOptions | undefined>(undefined));
const titleSourceId = inject<string | null>('titleSourceId', null);
const episodeEntityId = inject<string | undefined>(OFFER_ENTITYID_KEY, undefined);
const { placement } = getClickoutTrackingPlacement();

const { country, language } = useLanguageStore();
const { xServeTrack } = useAppStore();

const isMounted = useMounted();
const { isLoggedIn } = useUserStore();
const { activeVariantsWithControlGroup } = useExperimentStore();

const { trackTagEvent, withArbitrageTracking, isArbitrating } = useArbitrage();

const { inPromotionCatalogue, promotionOffer, catalogueItem } = usePromotion({
	title: () => props.title,
	offers: () => props.offers,
	hardcodedFallbackOfferId: () => props.hardcodedFallbackOfferId,
});

const { result: packageProviderPlans } = useQuery<GetPackageProviderPlansQuery>(
	GetPackageProviderPlansDocument,
	() => ({
		country: country.value,
		language: language.value,
		packageIds: [catalogueItem.value?.packageId ?? null].filter(Boolean),
	}),
	{
		enabled:
			(catalogueItem.value?.placement && !!catalogueItem.value) ||
			(!catalogueItem.value?.placement && !inPromotionCatalogue.value),
		errorPolicy: 'all',
	}
);

// GOLDEN_EXP
const buyboxGoldenExpVariant = computed(() => {
	if (
		!isMounted.value ||
		!['DE', 'ES'].includes(country.value) ||
		isLoggedIn.value ||
		isArbitrating.value ||
		props.title.objectType !== ObjectType.Movie ||
		inPromotionCatalogue.value
	)
		return null;
	return activeVariantsWithControlGroup.value[BuyboxGoldenExp] as BuyboxGoldenExpVariants;
});

// CTA has a border, and different text. Fallback only.
const isHighlighted = computed(
	() =>
		promotionOffer?.value?.id === FALLBACK_PROMOTION_OFFER &&
		(props.title.__typename === 'Show' || props.title.__typename === 'Season')
);

const isHighlightedCTA = computed(() => {
	if (!buyboxGoldenExpVariant.value || buyboxGoldenExpVariant.value === 'control') {
		return !isHighlighted.value;
	}
	// Variants with highlighted CTA
	return ['variant_1', 'variant_2'].includes(buyboxGoldenExpVariant.value || '');
});

const isHighlightedText = computed(() => {
	if (!buyboxGoldenExpVariant.value || buyboxGoldenExpVariant.value === 'control') {
		return isHighlighted.value;
	}
	// Variants with highlighted text
	return ['variant_1', 'variant_2'].includes(buyboxGoldenExpVariant.value || '');
});

const isWatchNowCTA = computed(() => {
	return ['variant_2'].includes(buyboxGoldenExpVariant.value || '');
});
// GOLDEN_EXP

const offerEntityId = computed(() => episodeEntityId ?? props.title.id);

const crunchyRollUrl = computed(() => {
	const url = props.offers.find(offer => offer.package.packageId === 283)?.standardWebURL;
	if (!url) return 'http://crunchyroll.pxf.io/xk92Nv';

	return url.includes('crunchyroll.pxf.io') ? url : `http://crunchyroll.pxf.io/xk92Nv?u=${url}`;
});

const packagePlan = computed(() => {
	const mainPackage = inPromotionCatalogue.value
		? promotionOffer.value?.package
		: packageProviderPlans.value?.packages?.[0];

	if (!mainPackage) return null;

	return getPlanOffer(mainPackage);
});

const rowNumber = computed(() => {
	if (container.value == null) return undefined;

	const parentBuyBox = container.value.closest?.('.buybox-selector') ?? null;
	const buyBoxRows = Array.from(parentBuyBox?.querySelectorAll('.buybox-selector > *') ?? []);
	const index = buyBoxRows.findIndex(row => row.contains(container.value));

	return index === -1 ? undefined : index;
});

/** Applies `uct_country`, `rowNumber`, `offerLocation`. */
function clickoutBase(u: ClickoutUrl) {
	return u
		.set('uct_country', country.value)
		.addContexts(titleContext.value)
		.set('uct_ft', '1')
		.setTitleSourceId(titleSourceId)
		.updateContext('ClickoutContextGraphql', 'rowNumber', rowNumber.value)
		.updateContext('ClickoutContextGraphql', 'filterOption', filterOption.value)
		.setForTracking('offerLocation', OfferLocation.FREE_TRIAL)
		.updateContext('TitleContextGraphql', 'offerEntityId', offerEntityId.value)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('ClickoutContextGraphql', 'clickoutType', 'free_trial')
		.setCanary(xServeTrack.value);
}

const hasAppleTvPlusOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 350));
const hasCrunchyRollOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 283));
const hasHuluOffer = computed(() => props.offers?.some(offer => offer.package.packageId === 15));
// BUYBOX_LOGGED_IN_EXP
const { getProviderAffiliateParams } = useBuyboxLoggedInExp();
const { allProvidersById } = useConstantStore();
// BUYBOX_LOGGED_IN_EXP

const { expActiveVariant, getClickoutUrl } = useSingleQtvExp();
const clickoutLink = computed(() => {
	if (!promotionOffer.value) return null;

	const packageId = promotionOffer.value?.package.packageId ?? 350;

	// // temporary bypass of uct for Crunchyroll
	// APPLE_CHANNELS_EXP
	if (packageId === 283 && !props.appleChannelsExpVariant) {
		// APPLE_CHANNELS_EXP
		return new ClickoutUrl({ ...promotionOffer.value, standardWebURL: crunchyRollUrl.value }, { isFreeTrial: true })
			.apply(clickoutBase)
			.set('uct_ft', '1');
	}

	if (isArbitrating.value) {
		const arbitrageUrl = withArbitrageTracking(promotionOffer);
		if (arbitrageUrl) {
			return new ClickoutUrl(
				{ ...promotionOffer.value, standardWebURL: arbitrageUrl.toString() },
				{ skipRedirect: true, isFreeTrial: true }
			).apply(clickoutBase);
		}
	}

	const clickoutUrl = new ClickoutUrl(promotionOffer.value, { isFreeTrial: true }).apply(clickoutBase);

	// BUYBOX_LOGGED_IN_EXP
	const shortName = allProvidersById.value?.[promotionOffer.value.package.packageId]?.shortName;
	const affiliateParams = getProviderAffiliateParams(shortName, 'FT');
	if (affiliateParams) {
		clickoutUrl.set('uct_ct', affiliateParams.ct);
		if (affiliateParams.itsct) {
			clickoutUrl.set('uct_itsct', affiliateParams.itsct);
		}
	}
	// BUYBOX_LOGGED_IN_EXP

	// GOLDEN_EXP
	if (buyboxGoldenExpVariant.value) {
		const isFallback = promotionOffer.value?.id === FALLBACK_PROMOTION_OFFER;
		if (isFallback) {
			const {
				variant,
				country: mCountry,
				number,
			} = getGolgenExpTagInformation(buyboxGoldenExpVariant.value, country.value);

			// Prime
			const packageId = promotionOffer.value?.package.packageId;
			if ([9, 119].includes(packageId)) {
				const clickoutUrl = new ClickoutUrl(promotionOffer.value, {
					isFreeTrial: true,
					skipYieldOptimization: true,
				})
					.apply(clickoutBase)
					.setOnOffer('tag', `just${mCountry}-ftexp-${variant}-${number}`);

				clickoutUrl.set('uct_ct', 'fallback');
				return clickoutUrl;
			}
		}
	}

	// APPLE_CHANNELS_EXP
	if (props.appleChannelsExpVariant) {
		const url = new ClickoutUrl(promotionOffer.value, { skipYieldOptimization: true, isFreeTrial: true }).apply(
			clickoutBase
		);
		if ([1854, 642].includes(promotionOffer.value?.package.packageId)) {
			url.set('uct_ct', `applechannel-${props.appleChannelsExpVariant.replace('ariant_', '')}`).set(
				'uct_itsct',
				'justwatch_tv'
			);
		}

		if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
			url.setOnOffer('tag', getAppleChannelsExpAmazonTag(country.value));

			url.set('r', url.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));

			if (!inPromotionCatalogue.value) {
				url.set('uct_ct', 'fallback');
			}
		}

		if (promotionOffer.value?.package.packageId === 283) {
			const clickoutUrlCR = new ClickoutUrl(
				{ ...promotionOffer.value, standardWebURL: crunchyRollUrl.value },
				{ skipYieldOptimization: true }
			).apply(clickoutBase);
			return clickoutUrlCR.set('uct_ct', 'applechco').set('uct_ft', '0');
		}

		return url;
	}
	// APPLE_CHANNELS_EXP

	// GOLDEN_EXP

	if (promotionOffer.value?.package.packageId !== 350 && promotionOffer.value?.id === FALLBACK_PROMOTION_OFFER) {
		if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
			// SINGLE_QTV_EXP

			if (expActiveVariant.value) {
				//@ts-ignore
				const url = getClickoutUrl(promotionOffer.value, props.title);
				url.set('uct_ct', 'fallback');
				return url;
			}

			let offer = promotionOffer.value;
			const ctParam = primeImpactCountries.includes(country.value) ? 'bypass' : 'fallback';
			const amazonPrimeUrl = new ClickoutUrl(offer, { skipYieldOptimization: true, isFreeTrial: true })
				.set('uct_ct', ctParam)
				.apply(clickoutBase);
			//// BUYBOX_LOGGED_IN_EXP
			if (affiliateParams?.ct) {
				const finalUrl = new URL(amazonPrimeUrl.get('r')!);
				if (ctParam === 'bypass') {
					finalUrl.searchParams.set('subId3', affiliateParams.ct);
					amazonPrimeUrl.set('r', finalUrl.toString());
				} else if (ctParam === 'fallback') {
					if (finalUrl.searchParams.get('tag')) {
						finalUrl.searchParams.set('tag', affiliateParams.ct);
						amazonPrimeUrl.set('r', finalUrl.toString());
					} else {
						amazonPrimeUrl.set('uct_ct', affiliateParams.ct + '-fallback');
					}
				}
			}
			// BUYBOX_LOGGED_IN_EXP
			return amazonPrimeUrl.set(
				'r',
				amazonPrimeUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com')
			);
		}

		return clickoutUrl;
	}

	const conversionTrackingByPackageId: Record<number, Record<'param' | 'value', string | null>> = {
		350: { value: hasAppleTvPlusOffer.value ? 'atds' : 'natds', param: 'ct' },
		283: { value: hasCrunchyRollOffer.value ? 'free_trial' : null, param: 'subId3' },
		15: { value: hasHuluOffer.value ? 'free_trial' : null, param: 'sub1' },
	};

	const partnerTokenByPackageId: Record<number, Record<'param' | 'value', string>> = {
		350: {
			value: getATVPlustItsctParam(country.value, hasAppleTvPlusOffer.value),
			param: 'itsct',
		},
	};

	if (partnerTokenByPackageId[packageId]?.value) {
		clickoutUrl.setOnOffer(partnerTokenByPackageId[packageId].param, partnerTokenByPackageId[packageId].value);
	}

	if (!!conversionTrackingByPackageId[packageId] && conversionTrackingByPackageId[packageId].param) {
		clickoutUrl.setOnOffer(
			conversionTrackingByPackageId[packageId].param!,
			conversionTrackingByPackageId[packageId].value
		);
	}

	if ([9, 119].includes(promotionOffer.value?.package.packageId)) {
		clickoutUrl.set('r', clickoutUrl.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	return clickoutUrl;
});

const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => props.additionalContexts,
});

const onVisibilityChanged = (isVisible: boolean) => {
	if (!isVisible || !clickoutLink.value) return;

	onIntersection(clickoutLink.value, isVisible);
};

const observeVisibilityOptions = computed(() => ({
	...observableOptions,
	callback: (isVisible: boolean) => onVisibilityChanged(isVisible),
}));

const isModalProvider = computed(() => promotionOffer.value?.package.shortName === 'jwt');

function openModal() {
	if (!isModalProvider.value) return;
	ModalHelper.openModal(
		JwTVModal,
		{ offer: promotionOffer.value, title: props.title },
		{ cssClass: 'trailer-modal' },
		'',
		true,
		[]
	);

	if (clickoutLink.value) trackClickout(clickoutLink.value);
	onClickOut();
}

function getPlanOffer(pckg: TitleOfferFragment['package'] | GetPackageProviderPlansQuery['packages'][0]) {
	if (pckg.planOffers.length > 0) {
		const plan = pckg.planOffers.find(offer => offer.isTrial || offer.children.length > 0);
		if (plan && plan.children.length > 0) {
			return { ...plan.children[0], retailPrice: plan.retailPrice };
		}
		return plan;
	}
	return null;
}

const { trackSingleQtvExp } = useSingleQtvExp();

function onClickOut() {
	trackTagEvent(promotionOffer);

	trackSingleQtvExp({
		action: 'click',
		label: `clickout`,
	});

	emit('clickOut', {
		buyboxOffer: promotionOffer?.value,
		offerLocation: OfferLocation.FREE_TRIAL,
		row: container.value,
	});
}

return { __sfc: true,props, emit, container, titleContext, filterOption, titleSourceId, episodeEntityId, placement, country, language, xServeTrack, isMounted, isLoggedIn, activeVariantsWithControlGroup, trackTagEvent, withArbitrageTracking, isArbitrating, inPromotionCatalogue, promotionOffer, catalogueItem, packageProviderPlans, buyboxGoldenExpVariant, isHighlighted, isHighlightedCTA, isHighlightedText, isWatchNowCTA, offerEntityId, crunchyRollUrl, packagePlan, rowNumber, clickoutBase, hasAppleTvPlusOffer, hasCrunchyRollOffer, hasHuluOffer, getProviderAffiliateParams, allProvidersById, expActiveVariant, getClickoutUrl, clickoutLink, onIntersection, onVisibilityChanged, observeVisibilityOptions, isModalProvider, openModal, getPlanOffer, trackSingleQtvExp, onClickOut, ProviderIcon, PromotionRowOfferDescription, PromotionRowOfferLabel, PromotionRowCTA, ClickoutAnchor }
}

})